"use client";
import React from "react";

import { Box, BoxSx, FlagIconNameType } from "..";
import { useIconCache } from "../Icon/IconContext";

type FlagIconProps = {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  flag: FlagIconNameType;
} & React.ComponentProps<typeof Box>;

export const FlagIcon: React.FC<FlagIconProps> = ({
  size = "md",
  sx,
  flag,
  ...restProps
}) => {
  const { getIconComponent, cacheIconComponent } = useIconCache();
  const FlagIconVariant = getIconComponent(flag);

  React.useEffect(() => {
    cacheIconComponent(flag, FlagIconVariant);
  }, [flag, FlagIconVariant, cacheIconComponent]);

  // @ts-ignore - for borderRadius values
  const iconStyle = React.useMemo<BoxSx>(() => {
    switch (size) {
      case "xs":
        return {
          width: "spacing-5",
          height: "spacing-4",
          borderRadius: ".75px"
        };
      case "sm":
        return {
          width: "spacing-7",
          height: "spacing-6",
          borderRadius: "1px"
        };
      case "lg":
        return {
          width: "spacing-9",
          height: "spacing-8",
          borderRadius: "2px"
        };
      case "xl":
        return {
          width: "spacing-10",
          height: "spacing-9",
          borderRadius: "3px"
        };
      default:
        return {
          width: "spacing-8",
          height: "spacing-7",
          borderRadius: "1.5px"
        };
    }
  }, [size]);

  return (
    <Box
      as="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        ...iconStyle,
        ...sx
      }}
      {...restProps}
    >
      <FlagIconVariant />
    </Box>
  );
};
